import React, {Key, memo} from 'react';

import {ActivityModel, ActivityVisualTypesEnum} from '../../../../models/api/activityModel';
import {ChannelModel} from '../../../../models/api/channelModel';
import {ConversationActionContextsEnum} from '../../../../models/api/conversationCommonTypes';
import {ReminderModel} from '../../../../models/api/reminderModel';
import {ResourceMap} from '../../../../store/appTypes';
import {CellsContext} from '../../../../store/panels/cells/context/cellsContext';
import {ViewerPanelId} from '../../../../store/panels/panelStateBase';
import {ActivityViewerState} from '../../../../store/panels/viewer/activity/activityViewerState';
import {BasicViewerContainer} from './basic/basicViewerContainer';
import {CommentTombstoneContainer} from './comment/commentTombstoneContainer';
import {CommentViewerContainer} from './comment/commentViewerContainer';
import {MessageViewerContainer} from './message/messageViewerContainer';

/*
 * Props.
 */

interface ActivityViewerProps {
  panelId: ViewerPanelId;
  userId: number;
  cellsContext: CellsContext;
  conversationId: number;
  conversationActionContext: ConversationActionContextsEnum;
  isSpam: boolean;
  namespace: string;
  replyChannel?: ChannelModel;
  canceledReminder?: ReminderModel;
  activities: ReadonlyArray<ActivityModel>;
  activityStates: ResourceMap<ActivityViewerState>;
  activityIndex: number;
  containsNewCalendarEvent?: boolean;
  isUnread?: boolean;
  isNewUnread?: boolean;
  isFirstMessage?: boolean;
  isFirstComment?: boolean;
  isLastActivity?: boolean;
  isFirstActivity?: boolean;
  isOnlyMessage?: boolean;
  isOnlyActivity?: boolean;
  isVisible?: boolean;
  focusKey?: Key;
  isPersonalizedDemoInboxActive?: boolean;
  jumpKey?: Key;
  jumpSourceActivityId: number | undefined;
  isHighlighted: boolean;
  lastIncomeMessageActivityId?: number;
}

/*
 * Component.
 */

export const ActivityViewer = memo<ActivityViewerProps>((props) => {
  const {userId, activities, activityStates, activityIndex, lastIncomeMessageActivityId, ...otherProps} =
    props;

  const activity = activities[activityIndex];
  if (!activity) {
    throw new Error('An activity could not be found for this index.');
  }

  switch (activity.visualType) {
    case ActivityVisualTypesEnum.MESSAGE: {
      const previousActivity = activities[activityIndex - 1];
      const previousMessageUid = toMessageUid(previousActivity);

      const lastIncomeMessageActivity = activities.find(
        (activityItem) => activityItem.id === lastIncomeMessageActivityId,
      );
      const lastIncomeMessageUid = toMessageUid(lastIncomeMessageActivity);

      return (
        <MessageViewerContainer
          {...otherProps}
          messageUid={activity.messageUid}
          activityId={activity.id}
          state={activityStates[activity.id]}
          previousMessageUid={previousMessageUid}
          lastIncomeMessageUid={lastIncomeMessageUid}
        />
      );
    }

    case ActivityVisualTypesEnum.COMMENT:
      if (activity.isTombstoned) {
        return (
          <CommentTombstoneContainer activity={activity} previousActivity={activities[activityIndex - 1]} />
        );
      }

      return (
        <CommentViewerContainer
          activity={activity}
          conversationId={props.conversationId}
          isLastSendingComment={false}
          isNewUnread={Boolean(props.isNewUnread)}
          isVisible={Boolean(props.isVisible)}
          isFirstComment={Boolean(props.isFirstComment)}
          jumpKey={props.jumpKey}
          jumpSourceActivityId={props.jumpSourceActivityId}
          isHighlighted={props.isHighlighted}
          panelId={props.panelId}
          previousActivity={activities[activityIndex - 1]}
          conversationActionContext={props.conversationActionContext}
          cellsContext={props.cellsContext}
          canceledReminder={props.canceledReminder}
        />
      );

    case ActivityVisualTypesEnum.BASIC:
      return (
        <BasicViewerContainer
          {...props}
          activities={props.activities}
          activityStates={props.activityStates}
          activityIndex={props.activityIndex}
          focusKey={props.focusKey}
          activity={activity}
          isPreviousActivityBasic={isPreviousActivityBasic(activities[activityIndex - 1])}
          isPersonalizedDemoInboxActive={props.isPersonalizedDemoInboxActive}
          conversationActionContext={props.conversationActionContext}
          cellsContext={props.cellsContext}
          canceledReminder={props.canceledReminder}
        />
      );

    default:
      return null;
  }
});

/*
 * Activity helpers.
 */

/** Returns the messageUid of the activity if it's a message. */
function toMessageUid(activity?: ActivityModel) {
  if (!activity || activity.visualType !== ActivityVisualTypesEnum.MESSAGE) {
    return undefined;
  }

  return activity.messageUid;
}

/** Check if the previous activity is a basic activity. */
function isPreviousActivityBasic(previousActivity?: ActivityModel) {
  return Boolean(previousActivity && previousActivity.visualType === ActivityVisualTypesEnum.BASIC);
}
